import React from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {format} from 'date-fns';
import {useGate, useUnit} from 'effector-react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useHistory, useParams} from 'react-router-dom';
import * as yup from 'yup';

import {ROUTES} from '@app/routing';
import {colors} from '@app/styles/theme';
import {EMPTY_VALUE as EMPTY} from '@consts/common';
import {STATUS_COLOR_LIST} from '@consts/statuses';
import {
    buildParams,
    localizeTitle,
    parseJobParams,
    SCHEDULE_TYPES,
} from '@entities/job-templates/lib';
import {JobTemplateDetailsGate, jobTemplateDetailsModel} from '@entities/job-templates/model';
import {JobParamElement} from '@entities/job-templates/types';
import {Button, ButtonVariant} from '@shared/ui/Button';
import {Checkbox} from '@shared/ui/Checkbox';
import {Icon} from '@shared/ui/Icon';
import {Input} from '@shared/ui/Input';
import {ContentContainer, StatusWrapper} from '@shared/ui/PageComponents';
import {Select, SelectOption} from '@shared/ui/Select';
import {Textarea} from '@shared/ui/Textarea';
import {Title} from '@shared/ui/Title';

import {
    Actions,
    BackButton,
    CheckboxRow,
    Field,
    FieldError,
    Form,
    HeaderCard,
    HeaderRow,
    InfoGrid,
    InfoItem,
    InfoLabel,
    InfoValue,
    SectionCard,
    Wrapper,
} from './styled';

type JobTemplateFormValues = {
    description: string;
    cronExpression: string;
    isEnabled: boolean;
    params: Record<string, string>;
};

const defaultValues: JobTemplateFormValues = {
    description: '',
    cronExpression: '',
    isEnabled: false,
    params: {},
};

const formatDate = (value?: string | null) => {
    if (!value) return EMPTY;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return EMPTY;
    return format(date, 'dd.MM.yyyy');
};

const statusColor = (code?: string | null) =>
    code === 'ENABLED' ? STATUS_COLOR_LIST.GREEN : STATUS_COLOR_LIST.GRAY_ALT;

const requiredTrimmed = (message: string) =>
    yup
        .string()
        .transform((value) => String(value ?? '').trim())
        .required(message)
        .test('not-blank', message, (value) => String(value ?? '').trim().length > 0);

const getSchema = (t: (key: string) => string, params: JobParamElement[], hasSchedule: boolean) =>
    yup.object().shape({
        description: requiredTrimmed(t('enter-description')),
        cronExpression: hasSchedule ? requiredTrimmed(t('select-schedule')) : yup.string(),
        isEnabled: yup.boolean().required(),
        params: yup
            .object()
            .shape(
                Object.fromEntries(
                    params.map((param, index) => [
                        String(index),
                        param.type === 'number'
                            ? requiredTrimmed(t('enter-value')).matches(
                                  /^[0-9]+$/,
                                  t('enter-value'),
                              )
                            : requiredTrimmed(t('enter-value')),
                    ]),
                ),
            ),
    });

export const JobTemplateDetails = () => {
    const {jobTemplateId} = useParams<{jobTemplateId: string}>();
    const {t, i18n} = useTranslation();
    const history = useHistory();
    const model = useUnit(jobTemplateDetailsModel);
    const [isEdit, setIsEdit] = React.useState(false);

    useGate(JobTemplateDetailsGate, {jobTemplateId});

    const {template} = model.state;
    const status = model.state.activeStatuses.find(
        (item) => item.id === template?.status || item.code === template?.status,
    );
    const isEnabled = status?.code === 'ENABLED';
    const params = React.useMemo(() => parseJobParams(template), [template]);
    const hasSchedule = template?.cronExpression != null;

    const translateValidates = React.useCallback(
        (key: string) => t(`page_audit.page_jobs.validation.${key}`),
        [t, i18n.language],
    );
    const schema = React.useMemo(() => getSchema(translateValidates, params, hasSchedule), [
        hasSchedule,
        params,
        translateValidates,
    ]);

    const {control, handleSubmit, errors, reset} = useForm<JobTemplateFormValues>({
        resolver: yupResolver(schema),
        mode: 'all',
        defaultValues,
    });

    const formValues = React.useMemo<JobTemplateFormValues>(
        () =>
            template
                ? {
                      description:
                          template.description?.[i18n.language] ?? template.description?.en ?? '',
                      cronExpression: template.cronExpression ?? '',
                      isEnabled,
                      params: Object.fromEntries(
                          params.map((param, index) => [String(index), param.value]),
                      ),
                  }
                : defaultValues,
        [i18n.language, isEnabled, params, template],
    );

    React.useEffect(() => {
        reset(formValues);
        setIsEdit(false);
    }, [formValues, reset]);

    const scheduleOptions = React.useMemo(
        () =>
            SCHEDULE_TYPES.map((item) => ({
                label: localizeTitle(item.title, i18n.language),
                value: item.code,
            })),
        [i18n.language],
    );

    const onSubmit = React.useCallback(
        async (values: JobTemplateFormValues) => {
            if (!template) return;

            const statusToSave = model.state.activeStatuses.find((item) =>
                values.isEnabled ? item.code === 'ENABLED' : item.code === 'DISABLED',
            );

            const paramValues = Object.fromEntries(
                params.map((param, index) => [param.key, values.params[String(index)] ?? '']),
            );

            await model.saveJobTemplate({
                id: template.id,
                payload: {
                    title: template.title ?? {},
                    description: {
                        ...(template.description ?? {}),
                        [i18n.language]: values.description.trim(),
                    },
                    status: statusToSave?.id,
                    cronExpression: hasSchedule ? values.cronExpression : null,
                    params: buildParams(template.params, paramValues),
                    version: template.version,
                },
            });
            setIsEdit(false);
        },
        [hasSchedule, i18n.language, model, params, template],
    );

    return (
        <ContentContainer
            status={model.state.pending && !template ? 'loading' : 'default'}
            noPaddings
        >
            <Wrapper>
                <HeaderCard>
                    <BackButton
                        type="button"
                        onClick={() => history.push(ROUTES.PREFERENCES_JOB_TEMPLATES)}
                    >
                        <Icon name="arrowLeft" size={16} color={colors.brandPrimary} />
                        {t('page_audit.page_dda.tabs.task-templates')}
                    </BackButton>
                    <HeaderRow>
                        <Title size={4}>
                            {t('page_audit.terms.job-template-name-and-version-full', {
                                name: localizeTitle(template?.title, i18n.language),
                                version: template?.version ?? EMPTY,
                            })}
                        </Title>
                        {isEdit ? (
                            <Actions>
                                <Button
                                    size="sm"
                                    type="button"
                                    variant={ButtonVariant.Outline}
                                    disabled={model.state.pending}
                                    onClick={() => {
                                        reset(formValues);
                                        setIsEdit(false);
                                    }}
                                >
                                    {t('actions.cancel')}
                                </Button>
                                <Button
                                    size="sm"
                                    type="submit"
                                    form="job-template-form"
                                    isLoading={model.state.pending}
                                    disabled={model.state.pending}
                                >
                                    {t('actions.save')}
                                </Button>
                            </Actions>
                        ) : (
                            <Actions>
                                <Button
                                    size="sm"
                                    type="button"
                                    variant={ButtonVariant.Outline}
                                    disabled={!template || model.state.pending}
                                    onClick={() => setIsEdit(true)}
                                >
                                    {t('actions.edit')}
                                </Button>
                            </Actions>
                        )}
                        {status ? (
                            <StatusWrapper color={statusColor(status.code)}>
                                {localizeTitle(status.title, i18n.language)}
                            </StatusWrapper>
                        ) : null}
                    </HeaderRow>
                </HeaderCard>

                <Form id="job-template-form" onSubmit={handleSubmit(onSubmit)} noValidate>
                    <SectionCard>
                        <InfoGrid>
                            <InfoItem>
                                <InfoLabel>{t('page_audit.terms.job-type')}</InfoLabel>
                                <InfoValue>
                                    {localizeTitle(template?.title, i18n.language)}
                                </InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>{t('page_audit.terms.version')}</InfoLabel>
                                <InfoValue>{template?.version || EMPTY}</InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>{t('page_audit.terms.updated')}</InfoLabel>
                                <InfoValue>{formatDate(template?.updated)}</InfoValue>
                            </InfoItem>
                            {isEdit ? (
                                <Field>
                                    <Controller
                                        name="description"
                                        control={control}
                                        render={({onChange, value, name}) => (
                                            <Textarea
                                                label={t('page_audit.terms.description')}
                                                name={name}
                                                value={value}
                                                onChange={onChange}
                                                rows={3}
                                                hasError={Boolean(errors.description)}
                                                isRequired
                                                disabled={model.state.pending}
                                            />
                                        )}
                                    />
                                    {errors.description && (
                                        <FieldError>{errors.description.message}</FieldError>
                                    )}
                                </Field>
                            ) : (
                                <InfoItem>
                                    <InfoLabel>{t('page_audit.terms.description')}</InfoLabel>
                                    <InfoValue>
                                        {localizeTitle(template?.description, i18n.language)}
                                    </InfoValue>
                                </InfoItem>
                            )}

                            {params.length ? (
                                <>
                                    <Title size={4}>{t('page_audit.terms.parameters')}</Title>
                                    {params.map((param, index) =>
                                        isEdit ? (
                                            <Field key={param.key}>
                                                <Controller
                                                    name={`params.${index}` as any}
                                                    control={control}
                                                    render={({onChange, value, name}) => (
                                                        <Input
                                                            size="sm"
                                                            label={localizeTitle(
                                                                param.title,
                                                                i18n.language,
                                                            )}
                                                            name={name}
                                                            type={param.type}
                                                            value={value}
                                                            onChange={onChange}
                                                            hasError={Boolean(
                                                                errors.params?.[String(index)],
                                                            )}
                                                            isRequired
                                                            disabled={model.state.pending}
                                                        />
                                                    )}
                                                />
                                                {errors.params?.[String(index)] && (
                                                    <FieldError>
                                                        {errors.params[String(index)]?.message}
                                                    </FieldError>
                                                )}
                                            </Field>
                                        ) : (
                                            <InfoItem key={param.key}>
                                                <InfoLabel>
                                                    {localizeTitle(param.title, i18n.language)}
                                                </InfoLabel>
                                                <InfoValue>{param.value || EMPTY}</InfoValue>
                                            </InfoItem>
                                        ),
                                    )}
                                </>
                            ) : null}

                            {hasSchedule &&
                                (isEdit ? (
                                    <Field>
                                        <Controller
                                            name="cronExpression"
                                            control={control}
                                            render={({onChange, value}) => (
                                                <Select
                                                    size="sm"
                                                    menuPlacement="top"
                                                    label={t('page_audit.terms.schedule')}
                                                    options={scheduleOptions}
                                                    value={scheduleOptions.find(
                                                        (item) => item.value === value,
                                                    )}
                                                    onChange={(option: unknown) =>
                                                        onChange(
                                                            (option as SelectOption<string> | null)
                                                                ?.value ?? '',
                                                        )
                                                    }
                                                    hasError={Boolean(errors.cronExpression)}
                                                    isRequired
                                                    disabled={model.state.pending}
                                                />
                                            )}
                                        />
                                        {errors.cronExpression && (
                                            <FieldError>{errors.cronExpression.message}</FieldError>
                                        )}
                                    </Field>
                                ) : (
                                    <InfoItem>
                                        <InfoLabel>{t('page_audit.terms.schedule')}</InfoLabel>
                                        <InfoValue>
                                            {localizeTitle(
                                                SCHEDULE_TYPES.find(
                                                    (item) =>
                                                        item.code === template?.cronExpression,
                                                )?.title,
                                                i18n.language,
                                            )}
                                        </InfoValue>
                                    </InfoItem>
                                ))}

                            {isEdit ? (
                                <Field>
                                    <Controller
                                        name="isEnabled"
                                        control={control}
                                        render={({onChange, value, name}) => (
                                            <CheckboxRow>
                                                <Checkbox
                                                    name={name}
                                                    checked={Boolean(value)}
                                                    disabled={model.state.pending}
                                                    onChange={(event) =>
                                                        onChange(event.target.checked)
                                                    }
                                                />
                                                {t('actions.activate')}
                                            </CheckboxRow>
                                        )}
                                    />
                                </Field>
                            ) : null}
                        </InfoGrid>
                    </SectionCard>
                </Form>
            </Wrapper>
        </ContentContainer>
    );
};
